import React, { useRef, useState } from "react";
import api, { formatTL } from "@/lib/api";
import { toast } from "sonner";
import { MediaLibrary } from "@/components/MediaLibrary";
import { Save, X, Loader2, Trash2 } from "lucide-react";

export const STOCK_OPTS = [
  { v: "in_stock", l: "Stokta", c: "bg-[#16A34A]" },
  { v: "out_of_stock", l: "Stokta Yok", c: "bg-[#71717A]" },
  { v: "coming_soon", l: "Çok Yakında", c: "bg-[#C8A96A]" },
];

export const EMPTY_PRODUCT = {
  name_tr: "", name_en: "", description_tr: "", description_en: "",
  sku: "", code: "", category_id: "", price: 0, discounted_price: null, stock: 0,
  images: [], features: [], specifications: {}, brand: "",
  is_featured: false, is_new: false, is_bestseller: false,
  status: "draft", stock_status: "in_stock",
  seo_title_tr: "", seo_title_en: "", seo_description_tr: "", seo_description_en: "", seo_slug: "",
};

export default function ProductEditor({ product, initial, cats, onClose, onSaved }) {
  const isEdit = !!product;
  const [form, setForm] = useState(() => ({ ...EMPTY_PRODUCT, ...(initial || {}), specifications: initial?.specifications || {} }));
  const [tab, setTab] = useState("general");
  const [busy, setBusy] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const fileRef = useRef(null);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        discounted_price: form.discounted_price ? parseFloat(form.discounted_price) : null,
        stock: parseInt(form.stock) || 0,
      };
      if (isEdit) await api.put(`/products/${product.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Kaydedildi");
      onSaved();
    } catch (e) { toast.error("Kayıt hatası: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const uploadFiles = async (files) => {
    const urls = [];
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload?folder=products", fd, { headers: { "Content-Type": "multipart/form-data" } });
      urls.push(data.url);
    }
    setForm((p) => ({ ...p, images: [...p.images, ...urls] }));
    toast.success(`${urls.length} resim eklendi`);
  };

  const removeImage = (i) => setForm((p) => ({ ...p, images: p.images.filter((_, idx) => idx !== i) }));

  const tabs = [["general", "Genel"], ["pricing", "Fiyat & Stok"], ["images", "Görseller"], ["specs", "Özellikler"], ["seo", "SEO"]];

  return (
    <div className="fixed inset-0 bg-black/50 z-[80] flex items-stretch" onClick={onClose}>
      <div data-testid="product-editor" className="bg-white w-full max-w-5xl ml-auto h-full overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border p-4 flex items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl">{isEdit ? "Ürünü Düzenle" : "Yeni Ürün"}</h2>
            <p className="text-[10px] text-[#71717A] uppercase tracking-wider mt-0.5">{product?.sku || "Yeni"}</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="editor-save" onClick={save} disabled={busy}
              className="bg-[#0A0A0A] hover:bg-[#C8A96A] text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Kaydet
            </button>
            <button onClick={onClose} className="p-2 hover:bg-[#FAF8F3]" data-testid="editor-close"><X size={18} /></button>
          </div>
        </div>

        <div className="border-b border-border px-4 flex gap-1 text-xs">
          {tabs.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-4 py-3 uppercase tracking-wider border-b-2 ${tab === k ? "border-[#C8A96A] text-[#C8A96A] font-medium" : "border-transparent text-[#71717A] hover:text-[#0A0A0A]"}`}>
              {l}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {tab === "general" && <GeneralTab form={form} setForm={setForm} cats={cats} />}
          {tab === "pricing" && <PricingTab form={form} setForm={setForm} />}
          {tab === "images" && <ImagesTab form={form} setForm={setForm} fileRef={fileRef} uploadFiles={uploadFiles} libOpen={libOpen} setLibOpen={setLibOpen} removeImage={removeImage} />}
          {tab === "specs" && <SpecsTab form={form} setForm={setForm} />}
          {tab === "seo" && <SeoTab form={form} setForm={setForm} />}
        </div>
      </div>
    </div>
  );
}

function GeneralTab({ form, setForm, cats }) {
  return (
    <div className="grid sm:grid-cols-2 gap-5 max-w-3xl">
      <TextField testid="ed-name-tr" label="Ad (TR) *" value={form.name_tr} onChange={(v) => setForm({ ...form, name_tr: v })} />
      <TextField testid="ed-name-en" label="Name (EN) *" value={form.name_en} onChange={(v) => setForm({ ...form, name_en: v })} />
      <TextField testid="ed-sku" label="SKU *" value={form.sku} onChange={(v) => setForm({ ...form, sku: v })} />
      <TextField testid="ed-code" label="Ürün Kodu" value={form.code} onChange={(v) => setForm({ ...form, code: v })} />
      <TextField testid="ed-brand" label="Marka" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} />
      <div>
        <Label l="Kategori *" />
        <select data-testid="ed-category" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}
          className="w-full border-b border-[#0A0A0A] bg-transparent py-2 text-sm focus:outline-none focus:border-[#C8A96A]">
          <option value="">Seç…</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name_tr}</option>)}
        </select>
      </div>
      <TextArea col2 testid="ed-desc-tr" label="Açıklama (TR)" value={form.description_tr} onChange={(v) => setForm({ ...form, description_tr: v })} rows={5} />
      <TextArea col2 testid="ed-desc-en" label="Description (EN)" value={form.description_en} onChange={(v) => setForm({ ...form, description_en: v })} rows={5} />
      <div className="sm:col-span-2 flex flex-wrap gap-4 text-sm">
        <FlagToggle label="Yayında" value={form.status === "published"} onChange={(v) => setForm({ ...form, status: v ? "published" : "draft" })} testid="ed-published" />
        <FlagToggle label="Öne Çıkan" value={form.is_featured} onChange={(v) => setForm({ ...form, is_featured: v })} testid="ed-featured" />
        <FlagToggle label="Yeni Gelen" value={form.is_new} onChange={(v) => setForm({ ...form, is_new: v })} testid="ed-new" />
        <FlagToggle label="Çok Satan" value={form.is_bestseller} onChange={(v) => setForm({ ...form, is_bestseller: v })} testid="ed-bestseller" />
      </div>
    </div>
  );
}

function PricingTab({ form, setForm }) {
  return (
    <div className="grid sm:grid-cols-2 gap-5 max-w-2xl">
      <NumField testid="ed-price" label="Fiyat (₺) *" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
      <NumField testid="ed-discounted" label="İndirimli Fiyat (₺)" value={form.discounted_price || ""} onChange={(v) => setForm({ ...form, discounted_price: v })} />
      <NumField testid="ed-stock" label="Stok Miktarı" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} />
      <div>
        <Label l="Stok Durumu" />
        <select data-testid="ed-stock-status" value={form.stock_status} onChange={(e) => setForm({ ...form, stock_status: e.target.value })}
          className="w-full border-b border-[#0A0A0A] bg-transparent py-2 text-sm focus:outline-none focus:border-[#C8A96A]">
          {STOCK_OPTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
      </div>
      {form.supplier_price ? (
        <div className="sm:col-span-2 bg-[#FAF8F3] p-3 text-xs text-[#52525B]">
          Tedarikçi Referans Fiyatı: <strong>{formatTL(form.supplier_price)}</strong> · Son içe aktarma: {form.last_imported_at?.slice(0, 16) || "—"}
        </div>
      ) : null}
    </div>
  );
}

function ImagesTab({ form, setForm, fileRef, uploadFiles, libOpen, setLibOpen, removeImage }) {
  const [dragIdx, setDragIdx] = useState(null);
  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (i) => {
    if (dragIdx == null || dragIdx === i) { setDragIdx(null); return; }
    setForm((p) => {
      const next = [...p.images];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(i, 0, moved);
      return { ...p, images: next };
    });
    setDragIdx(null);
  };
  return (
    <div>
      <div className="flex gap-3 mb-4 items-center">
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={(e) => e.target.files && uploadFiles(Array.from(e.target.files))}
          className="text-xs" data-testid="ed-upload" />
        <button data-testid="ed-library" onClick={() => setLibOpen(true)}
          className="text-[10px] uppercase tracking-[0.2em] text-[#C8A96A] hover:text-[#A6864A]">Kütüphaneden Ekle</button>
        <span className="text-[10px] text-[#71717A] uppercase tracking-wider ml-auto">Sürükleyerek sırala · 1. resim ana resimdir</span>
      </div>
      {libOpen && <MediaLibrary mode="picker" folder="products"
        onSelect={(url) => setForm((p) => ({ ...p, images: [...p.images, url] }))}
        onClose={() => setLibOpen(false)} />}
      <div className="grid grid-cols-4 gap-3">
        {form.images.map((img, i) => (
          <ImageTile key={i} img={img} isMain={i === 0} onRemove={() => removeImage(i)} index={i}
            onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} isDragging={dragIdx === i} />
        ))}
        {form.images.length === 0 && (
          <div className="col-span-4 text-center py-12 text-sm text-[#71717A] border border-dashed border-border">Henüz görsel yok</div>
        )}
      </div>
    </div>
  );
}

function ImageTile({ img, isMain, onRemove, index, onDragStart, onDragOver, onDrop, isDragging }) {
  const src = img.startsWith("http") ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`;
  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={onDragOver}
      onDrop={() => onDrop(index)}
      data-testid={`img-tile-${index}`}
      className={`relative group aspect-square border cursor-move transition-all ${isMain ? "border-[#C8A96A] border-2" : "border-border"} ${isDragging ? "opacity-40 scale-95" : ""}`}
    >
      <img src={src} alt="" className="w-full h-full object-cover pointer-events-none" />
      {isMain && <span className="absolute top-1 left-1 bg-[#C8A96A] text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5">Ana</span>}
      <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5">#{index + 1}</span>
      <button onClick={onRemove} className="absolute top-1 right-1 bg-[#EF4444] text-white p-1 opacity-0 group-hover:opacity-100" data-testid={`img-remove-${index}`}><Trash2 size={10} /></button>
    </div>
  );
}

function SpecsTab({ form, setForm }) {
  return (
    <div className="max-w-3xl">
      <p className="text-xs text-[#71717A] mb-3">Teknik özellikler (key/value). İçe aktarılan ürünler için otomatik doldurulur.</p>
      <SpecsEditor specs={form.specifications} onChange={(s) => setForm({ ...form, specifications: s })} />
    </div>
  );
}

function SeoTab({ form, setForm }) {
  return (
    <div className="grid sm:grid-cols-2 gap-5 max-w-3xl">
      <TextField testid="ed-seo-slug" label="URL Slug" value={form.seo_slug} onChange={(v) => setForm({ ...form, seo_slug: v })} />
      <div />
      <TextField testid="ed-seo-title-tr" label="SEO Başlık (TR)" value={form.seo_title_tr} onChange={(v) => setForm({ ...form, seo_title_tr: v })} />
      <TextField testid="ed-seo-title-en" label="SEO Title (EN)" value={form.seo_title_en} onChange={(v) => setForm({ ...form, seo_title_en: v })} />
      <TextArea col2 label="SEO Açıklama (TR)" value={form.seo_description_tr} onChange={(v) => setForm({ ...form, seo_description_tr: v })} rows={3} />
      <TextArea col2 label="SEO Description (EN)" value={form.seo_description_en} onChange={(v) => setForm({ ...form, seo_description_en: v })} rows={3} />
    </div>
  );
}

function Label({ l }) { return <label className="block text-[10px] uppercase tracking-[0.2em] mb-1 text-[#71717A]">{l}</label>; }

function TextField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label l={label} />
      <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full border-b border-[#0A0A0A] bg-transparent py-2 text-sm focus:outline-none focus:border-[#C8A96A]" />
    </div>
  );
}

function TextArea({ label, value, onChange, testid, rows = 3, col2 }) {
  return (
    <div className={col2 ? "sm:col-span-2" : ""}>
      <Label l={label} />
      <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows}
        className="w-full border border-border p-2 text-sm focus:outline-none focus:border-[#C8A96A]" />
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label l={label} />
      <input data-testid={testid} type="number" step="0.01" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full border-b border-[#0A0A0A] bg-transparent py-2 text-sm focus:outline-none focus:border-[#C8A96A]" />
    </div>
  );
}

function FlagToggle({ label, value, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      <span>{label}</span>
    </label>
  );
}

function SpecsEditor({ specs, onChange }) {
  const entries = Object.entries(specs || {});
  const [newKey, setNewKey] = useState("");
  const [newVal, setNewVal] = useState("");
  const addSpec = () => {
    if (!newKey) return;
    onChange({ ...(specs || {}), [newKey]: newVal });
    setNewKey(""); setNewVal("");
  };
  return (
    <div className="space-y-2">
      {entries.map(([k, v], i) => (
        <SpecRow key={i} k={k} v={v} specs={specs} onChange={onChange} />
      ))}
      <div className="flex gap-2 items-center pt-3 border-t border-border">
        <input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="Özellik"
          className="w-1/3 border-b border-[#0A0A0A] py-1.5 px-2 text-sm focus:outline-none focus:border-[#C8A96A]" />
        <input value={newVal} onChange={(e) => setNewVal(e.target.value)} placeholder="Değer"
          className="flex-1 border-b border-[#0A0A0A] py-1.5 px-2 text-sm focus:outline-none focus:border-[#C8A96A]" />
        <button onClick={addSpec} className="bg-[#0A0A0A] hover:bg-[#C8A96A] text-white px-3 py-1.5 text-xs uppercase tracking-wider">Ekle</button>
      </div>
    </div>
  );
}

function SpecRow({ k, v, specs, onChange }) {
  const onValChange = (e) => onChange({ ...specs, [k]: e.target.value });
  const onDel = () => { const next = { ...specs }; delete next[k]; onChange(next); };
  return (
    <div className="flex gap-2 items-center">
      <input value={k} disabled className="w-1/3 border-b border-border bg-[#FAF8F3] py-1.5 px-2 text-sm" />
      <input value={v} onChange={onValChange} className="flex-1 border-b border-[#0A0A0A] py-1.5 px-2 text-sm focus:outline-none focus:border-[#C8A96A]" />
      <button onClick={onDel} className="text-[#EF4444]"><Trash2 size={14} /></button>
    </div>
  );
}
